/**
 * AS CONTAS — UM MÓDULO SÓ
 *
 * Toda conta do sistema mora aqui e é importada por todas as telas. A regra
 * está no CLAUDE.md e é o motivo de este projeto não repetir o padrão do
 * silos-app: copiar fórmula em oito arquivos garante que um dia elas divirjam,
 * e ninguém descobre qual das oito está certa.
 *
 * A dedução de cada fórmula, com exemplo numérico e caso-limite, está em
 * `docs/REGRAS-DE-CALCULO.md`. Se este arquivo e aquele documento discordarem,
 * o documento vence e este arquivo está com bug.
 */

import {
  type ConversaoAlimentar, type Dias, type Fracao, type Gramas, type Hectares,
  type KgPorHaDia, type MetrosQuadrados, type Peixes, type Quilos,
  type ToneladasPorHectare,
  fracao, g, ha, kg, kgHaDia, m2ParaHa, tHa,
} from './unidades'

// ---------------------------------------------------------------------------
// Biomassa e densidade
// ---------------------------------------------------------------------------

/** Peso total de peixe no tanque. */
export function biomassa(quantidade: Peixes, pesoMedio: Gramas): Quilos {
  return kg((quantidade * pesoMedio) / 1000)
}

/** Densidade em toneladas por hectare — como o histórico da fazenda mede. */
export function densidade(biomassaKg: Quilos, area: MetrosQuadrados): ToneladasPorHectare {
  const areaHa = m2ParaHa(area)
  if (areaHa <= 0) return tHa(0)
  return tHa(biomassaKg / areaHa / 1000)
}

/** Espaço por peixe — a outra leitura da mesma densidade. */
export function m2PorPeixe(area: MetrosQuadrados, quantidade: Peixes): number {
  return quantidade > 0 ? area / quantidade : 0
}

// ---------------------------------------------------------------------------
// Estimativa de peso — o botão da beira do tanque
//
// Não existe curva de crescimento como entrada (regra 1). Quem informa a
// conversão alimentar plausível é a pessoa, na hora, porque ela conhece aquele
// tanque naquela época melhor que qualquer tabela.
// ---------------------------------------------------------------------------

export interface EstimativaDePeso {
  readonly caInformada: ConversaoAlimentar
  readonly ganhoDeBiomassa: Quilos
  readonly pesoMedioEstimado: Gramas
}

/**
 * Converte ração consumida em peso estimado, usando a CA que a pessoa informou.
 *
 *   ganho de biomassa  = ração ÷ CA
 *   peso médio         = peso de entrada + (ganho ÷ peixes) × 1000
 *
 * `pesoDeEntrada` é o peso com que o peixe entrou NESTE ciclo — não o do
 * alevino. É o peixe que entrou nesta represa que está crescendo aqui.
 */
export function estimarPeso(
  racaoConsumida: Quilos,
  caInformada: ConversaoAlimentar,
  peixesNoTanque: Peixes,
  pesoDeEntrada: Gramas,
): EstimativaDePeso {
  if (caInformada <= 0 || peixesNoTanque <= 0) {
    return { caInformada, ganhoDeBiomassa: kg(0), pesoMedioEstimado: pesoDeEntrada }
  }
  const ganho = kg(racaoConsumida / caInformada)
  const ganhoPorPeixe = (ganho / peixesNoTanque) * 1000
  return {
    caInformada,
    ganhoDeBiomassa: ganho,
    pesoMedioEstimado: g(pesoDeEntrada + ganhoPorPeixe),
  }
}

/**
 * Mostrar uma faixa é mais honesto que mostrar um número. Transforma um palpite
 * escondido numa incerteza explícita — que é o que a estimativa realmente é.
 */
export function estimarFaixa(
  racaoConsumida: Quilos,
  cas: readonly ConversaoAlimentar[],
  peixesNoTanque: Peixes,
  pesoDeEntrada: Gramas,
): EstimativaDePeso[] {
  return cas.map((c) => estimarPeso(racaoConsumida, c, peixesNoTanque, pesoDeEntrada))
}

// ---------------------------------------------------------------------------
// Conversão alimentar realizada — só existe com balança dos dois lados
// ---------------------------------------------------------------------------

export function conversaoAlimentar(
  racaoConsumida: Quilos,
  biomassaEntrada: Quilos,
  biomassaSaida: Quilos,
): ConversaoAlimentar | null {
  const ganho = biomassaSaida - biomassaEntrada
  if (ganho <= 0) return null // ciclo sem ganho: a CA não significa nada
  return (racaoConsumida / ganho) as ConversaoAlimentar
}

// ---------------------------------------------------------------------------
// Fechamento
// ---------------------------------------------------------------------------

export function sobrevivencia(entraram: Peixes, sairam: Peixes): Fracao | null {
  return entraram > 0 ? fracao(sairam / entraram) : null
}

/**
 * Saldo residual: peixe que sumiu sem registro que explique.
 * Negativo significa que saiu mais do que entrou — o que é comum e não é erro
 * de sistema (sobra de remessa anterior, ou contagem de entrada baixa).
 * A tela precisa tratar os dois sinais de forma diferente.
 */
export function saldoResidual(
  entraram: Peixes, mortalidade: Peixes, perdas: Peixes, sairam: Peixes,
): number {
  return entraram - mortalidade - perdas - sairam
}

// ---------------------------------------------------------------------------
// Ociosidade — conta-se quilo, nunca trato
//
// Contar tratos quebra na primeira semana: o tratador que oferta a ração do dia
// inteiro de manhã, porque à tarde a água piorou, apareceria como "meio dia
// perdido" sem ter perdido nada.
// ---------------------------------------------------------------------------

export interface MetaDoDia {
  readonly demandaBiologica: Quilos
  readonly tetoAmbiental: Quilos
  readonly meta: Quilos
  readonly quemManda: 'peixe' | 'agua'
}

/**
 * Manda o menor dos dois: o que o peixe come, ou o que a água depura.
 * `pctPesoVivo` vem da tabela de arraçoamento; `teto` da tabela de teto
 * ambiental, que varia com a estação. Ambos são dados, nunca código.
 */
export function metaDoDia(
  biomassaAtual: Quilos,
  pctPesoVivo: Fracao,
  teto: KgPorHaDia,
  area: MetrosQuadrados,
): MetaDoDia {
  const demanda = kg(biomassaAtual * pctPesoVivo)
  const tetoKg = kg(teto * m2ParaHa(area))
  const manda = demanda <= tetoKg ? 'peixe' : 'agua'
  return {
    demandaBiologica: demanda,
    tetoAmbiental: tetoKg,
    meta: kg(Math.min(demanda, tetoKg)),
    quemManda: manda,
  }
}

/** Fração do dia perdida — 0 é dia cheio, 1 é dia sem nada. Nunca sim/não. */
export function ociosidade(ofertado: Quilos, meta: Quilos): Fracao {
  if (meta <= 0) return fracao(0) // água impedia tratar: o tanque fez o possível
  return fracao(Math.max(0, 1 - ofertado / meta))
}

/**
 * As duas perdas, que apontam para decisões diferentes:
 *   operacional → gestão (escala, ração em falta, equipamento)
 *   ambiental   → infraestrutura (o preço de não ter aeração nem renovação)
 */
export function perdas(m: MetaDoDia, ofertado: Quilos): {
  operacional: Quilos
  ambiental: Quilos
} {
  return {
    operacional: kg(Math.max(0, m.meta - ofertado)),
    ambiental: kg(Math.max(0, m.demandaBiologica - m.tetoAmbiental)),
  }
}

// ---------------------------------------------------------------------------
// Ambiente
// ---------------------------------------------------------------------------

export interface LimitesDeAgua {
  readonly temperaturaMinimaC: number
  readonly temperaturaMaximaC: number
  readonly oxigenioMinimoMgL: number
}

/** Valores de partida da Cunhataí. Editáveis por propriedade — nunca fixos. */
export const LIMITES_PADRAO: LimitesDeAgua = {
  temperaturaMinimaC: 26,
  temperaturaMaximaC: 36,
  oxigenioMinimoMgL: 2,
}

export interface LeituraDeAgua {
  readonly temperaturaC?: number
  readonly oxigenioMgL?: number
}

/**
 * Decide se o dia desaconselha tratar. Devolve o motivo junto, porque a tela
 * precisa dizer POR QUE — "não recomendável tratar" sem causa vira ruído que o
 * tratador aprende a ignorar.
 */
export function naoRecomendavelTratar(
  leitura: LeituraDeAgua,
  limites: LimitesDeAgua = LIMITES_PADRAO,
): { impedido: boolean; motivo: string | null } {
  const { temperaturaC, oxigenioMgL } = leitura
  if (oxigenioMgL !== undefined && oxigenioMgL < limites.oxigenioMinimoMgL) {
    return { impedido: true, motivo: `oxigênio em ${oxigenioMgL} mg/L` }
  }
  if (temperaturaC !== undefined && temperaturaC < limites.temperaturaMinimaC) {
    return { impedido: true, motivo: `água a ${temperaturaC} °C, fria demais` }
  }
  if (temperaturaC !== undefined && temperaturaC > limites.temperaturaMaximaC) {
    return { impedido: true, motivo: `água a ${temperaturaC} °C, quente demais` }
  }
  return { impedido: false, motivo: null }
}

// ---------------------------------------------------------------------------
// Tempo
// ---------------------------------------------------------------------------

/**
 * Tempo de estocagem conta do início DESTE ciclo — de quando o peixe entrou
 * naquele tanque, não da chegada do alevino. O tratador que vê 400 dias num
 * tanque de engorda de 240 para de confiar no número.
 */
export function diasDeEstocagem(inicioDoCiclo: Date, ate: Date = new Date()): Dias {
  const umDia = 24 * 60 * 60 * 1000
  const d = Math.floor((ate.getTime() - inicioDoCiclo.getTime()) / umDia)
  return (d > 0 ? d : 0) as Dias
}

// ---------------------------------------------------------------------------
// Carga da água — a série que o projeto existe para construir
// ---------------------------------------------------------------------------

export function cargaDoDia(ofertado: Quilos, area: MetrosQuadrados): KgPorHaDia {
  const areaHa: Hectares = m2ParaHa(area)
  return areaHa > 0 ? kgHaDia(ofertado / areaHa) : kgHaDia(0)
}

/** Referência medida no histórico da própria fazenda. Ver ANALISE-HISTORICA.md. */
export const TETO_OBSERVADO = {
  cheia: kgHaDia(76), // out–abr — p90 dos ciclos maduros
  seca: kgHaDia(65), // mai–set
  literatura: kgHaDia(40), // recomendação para viveiro sem aeração
} as const

export function estacaoDoMes(mes: number): 'cheia' | 'seca' {
  return mes >= 5 && mes <= 9 ? 'seca' : 'cheia'
}

export function areaEmHectares(area: MetrosQuadrados): Hectares {
  return m2ParaHa(area)
}

export { ha, kg, g }
